import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'slices';
import { LoadStatus } from 'types/common/apiStatus';
import { SelectedData } from 'types/common/FormTypes';

interface SelectedSubDistrict {
  value: string;
  label: string;
  zip: string;
}

interface LocationsState {
  provinces: SelectedData[];
  provincesStatus: LoadStatus;
  provincesError?: string | null;

  provincesShipping: SelectedData[];
  provincesShippingStatus: LoadStatus;
  provincesShippingError?: string | null;

  districts: SelectedData[];
  districtsStatus: LoadStatus;
  districtsError?: string | null;

  subDistricts: SelectedSubDistrict[];
  subDistrictsStatus: LoadStatus;
  subDistrictsError?: string | null;

  ProvincesOptions: SelectedData[];
  ProvincesShippingOptions: SelectedData[];
  DistrictsOptions: SelectedData[];
  SubDistrictsOptions: SelectedSubDistrict[];
}

export const initialState: LocationsState = {
  provinces: [],
  provincesStatus: 'idle',
  provincesError: null,

  provincesShipping: [],
  provincesShippingStatus: 'idle',
  provincesShippingError: null,

  districts: [],
  districtsStatus: 'idle',
  districtsError: null,

  subDistricts: [],
  subDistrictsStatus: 'idle',
  subDistrictsError: null,

  ProvincesOptions: [],
  ProvincesShippingOptions: [],
  DistrictsOptions: [],
  SubDistrictsOptions: [],
};

const LocationsSlice = createSlice({
  name: 'Locations',
  initialState,
  reducers: {
    // ===== Provinces =====
    fetchProvincesStart(state) {
      state.provincesStatus = 'loading';
      state.provincesError = null;
    },
    fetchProvincesSuccess(state, action: PayloadAction<SelectedData[]>) {
      state.provinces = action.payload;
      state.provincesStatus = 'success';
      state.provincesError = null;

      state.ProvincesOptions = action.payload;
    },
    fetchProvincesFailure(state, action: PayloadAction<string | undefined>) {
      state.provincesStatus = 'error';
      state.provincesError = action.payload ?? null;

      state.provinces = [];
      state.ProvincesOptions = [];
    },
    // ===== Provinces Shipping =====
    fetchProvincesShippingStart(state) {
      state.provincesShippingStatus = 'loading';
      state.provincesShippingError = null;
    },
    fetchProvincesShippingSuccess(
      state,
      action: PayloadAction<SelectedData[]>
    ) {
      state.provincesShipping = action.payload;
      state.provincesShippingStatus = 'success';
      state.provincesShippingError = null;

      state.ProvincesShippingOptions = action.payload;
    },
    fetchProvincesShippingFailure(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.provincesShippingStatus = 'error';
      state.provincesShippingError = action.payload ?? null;

      state.provincesShipping = [];
      state.ProvincesShippingOptions = [];
    },

    // ===== Districts =====
    fetchDistrictsStart(state) {
      state.districtsStatus = 'loading';
      state.districtsError = null;
    },
    fetchDistrictsSuccess(state, action: PayloadAction<SelectedData[]>) {
      state.districts = action.payload;
      state.districtsStatus = 'success';
      state.districtsError = null;

      state.DistrictsOptions = action.payload;
    },
    fetchDistrictsFailure(state, action: PayloadAction<string | undefined>) {
      state.districtsStatus = 'error';
      state.districtsError = action.payload ?? null;

      state.districts = [];
      state.DistrictsOptions = [];
    },

    // ===== Subdistricts =====
    fetchSubDistrictsStart(state) {
      state.subDistrictsStatus = 'loading';
      state.subDistrictsError = null;
    },
    fetchSubDistrictsSuccess(
      state,
      action: PayloadAction<SelectedSubDistrict[]>
    ) {
      state.subDistricts = action.payload;
      state.subDistrictsStatus = 'success';
      state.subDistrictsError = null;

      state.SubDistrictsOptions = action.payload;
    },
    fetchSubDistrictsFailure(state, action: PayloadAction<string | undefined>) {
      state.subDistrictsStatus = 'error';
      state.subDistrictsError = action.payload ?? null;

      state.subDistricts = [];
      state.SubDistrictsOptions = [];
    },

    setProvincesOptions(state, action: PayloadAction<SelectedData[]>) {
      state.provinces = action.payload;
      state.provincesStatus = 'success';
      state.provincesError = null;
      state.ProvincesOptions = action.payload;
    },
    setProvincesShippingOptions(state, action: PayloadAction<SelectedData[]>) {
      state.provincesShipping = action.payload;
      state.provincesShippingStatus = 'success';
      state.provincesShippingError = null;
      state.ProvincesShippingOptions = action.payload;
    },
    setDistrictsOptions(state, action: PayloadAction<SelectedData[]>) {
      state.districts = action.payload;
      state.districtsStatus = 'success';
      state.districtsError = null;
      state.DistrictsOptions = action.payload;
    },
    setSubDistrictsOptions(
      state,
      action: PayloadAction<SelectedSubDistrict[]>
    ) {
      state.subDistricts = action.payload;
      state.subDistrictsStatus = 'success';
      state.subDistrictsError = null;
      state.SubDistrictsOptions = action.payload;
    },

    resetProvinces(state) {
      state.provinces = [];
      state.ProvincesOptions = [];
      state.provincesStatus = 'idle';
      state.provincesError = null;
    },
    resetProvincesShipping(state) {
      state.provinces = [];
      state.ProvincesOptions = [];
      state.provincesStatus = 'idle';
      state.provincesError = null;
    },
    resetDistricts(state) {
      state.districts = [];
      state.DistrictsOptions = [];
      state.districtsStatus = 'idle';
      state.districtsError = null;
    },
    resetSubDistricts(state) {
      state.subDistricts = [];
      state.SubDistrictsOptions = [];
      state.subDistrictsStatus = 'idle';
      state.subDistrictsError = null;
    },
  },
});

export const {
  // new actions
  fetchProvincesStart,
  fetchProvincesSuccess,
  fetchProvincesFailure,
  fetchProvincesShippingStart,
  fetchProvincesShippingSuccess,
  fetchProvincesShippingFailure,
  fetchDistrictsStart,
  fetchDistrictsSuccess,
  fetchDistrictsFailure,
  fetchSubDistrictsStart,
  fetchSubDistrictsSuccess,
  fetchSubDistrictsFailure,

  // back-compat actions
  setProvincesOptions,
  setProvincesShippingOptions,
  setDistrictsOptions,
  setSubDistrictsOptions,

  // resets
  resetProvinces,
  resetProvincesShipping,
  resetDistricts,
  resetSubDistricts,
} = LocationsSlice.actions;

export default LocationsSlice.reducer;

// ===== Selectors (ergonomic) =====
export const selectProvinces = (s: RootState) =>
  s.Locations.provinces as SelectedData[];
export const selectProvincesStatus = (s: RootState) =>
  s.Locations.provincesStatus as LoadStatus;

export const selectProvincesShipping = (s: RootState) =>
  s.Locations.provincesShipping as SelectedData[];
export const selectProvincesShippingStatus = (s: RootState) =>
  s.Locations.provincesShippingStatus as LoadStatus;

export const selectDistricts = (s: RootState) =>
  s.Locations.districts as SelectedData[];
export const selectDistrictsStatus = (s: RootState) =>
  s.Locations.districtsStatus as LoadStatus;

export const selectSubDistricts = (s: RootState) =>
  s.Locations.subDistricts as SelectedSubDistrict[];
export const selectSubDistrictsStatus = (s: RootState) =>
  s.Locations.subDistrictsStatus as LoadStatus;
